import React, { useEffect, useState, useMemo } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  Trophy, Medal, TrendingUp, ShoppingBag, Gift, 
  DollarSign, Loader2, MapPin, Target, Zap, Award,
  ChevronDown, ChevronUp
} from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

interface SellerScoreData {
  seller_id: string;
  seller_name: string;
  ubicacion_tienda: string | null;
  // New score system
  total_score: number;
  membership_sale_points: number;
  redemption_points: number;
  passive_redemption_points: number;
  savings_points: number;
  cycle_milestone_points: number;
  achievement_points: number;
  // Raw data
  bronce_sales: number;
  plata_sales: number;
  oro_sales: number;
  total_canjes: number;
  total_ahorros: number;
}

const SCORE_CATEGORIES = [
  {
    key: 'membership_sale_points',
    label: 'Membresías',
    icon: ShoppingBag,
    color: 'text-amber-400',
    bgColor: 'bg-amber-500/20',
    description: 'Bronce=10pts, Plata=20pts, Oro=40pts'
  },
  {
    key: 'redemption_points',
    label: 'Canjes',
    icon: Gift,
    color: 'text-pink-400',
    bgColor: 'bg-pink-500/20',
    description: '8 pts por cada canje procesado'
  },
  {
    key: 'passive_redemption_points',
    label: 'Pasivos',
    icon: Zap,
    color: 'text-cyan-400',
    bgColor: 'bg-cyan-500/20',
    description: '2 pts por canjes en tus membresías'
  },
  {
    key: 'savings_points',
    label: 'Ahorros',
    icon: DollarSign,
    color: 'text-green-400',
    bgColor: 'bg-green-500/20',
    description: '10 pts por cada ₡10,000 ahorrados'
  },
  {
    key: 'cycle_milestone_points',
    label: 'Hitos Ciclo',
    icon: Target,
    color: 'text-blue-400',
    bgColor: 'bg-blue-500/20',
    description: 'Bonos mensuales: primera venta, racha, etc.'
  },
  {
    key: 'achievement_points',
    label: 'Logros Perm.',
    icon: Award,
    color: 'text-purple-400',
    bgColor: 'bg-purple-500/20',
    description: 'Logros acumulativos que nunca se pierden'
  },
];

export const ScoreSummaryTab: React.FC = () => {
  const { user, isAdmin, isVendedor } = useAuth();
  const [sellers, setSellers] = useState<SellerScoreData[]>([]);
  const [loading, setLoading] = useState(true);
  const [expandedSeller, setExpandedSeller] = useState<string | null>(null);
  
  // Sellers can only expand their own card
  const showRestrictedView = isVendedor && !isAdmin;

  useEffect(() => {
    fetchScoreData();
  }, []);

  const fetchScoreData = async () => {
    setLoading(true);
    try {
      // Use the RPC that already calculates all the scores
      const { data: rankingData, error } = await (supabase as any).rpc('get_seller_ranking_stats');
      
      if (error) {
        console.error('Error fetching ranking stats:', error);
        setLoading(false);
        return;
      }

      if (rankingData && Array.isArray(rankingData)) {
        const formattedData: SellerScoreData[] = rankingData.map((s: any) => ({
          seller_id: s.seller_id,
          seller_name: s.seller_name || 'Vendedor',
          ubicacion_tienda: s.ubicacion_tienda,
          total_score: s.total_score || 0,
          membership_sale_points: s.membership_sale_points || 0,
          redemption_points: s.redemption_points || 0,
          passive_redemption_points: s.passive_redemption_points || 0,
          savings_points: s.savings_points || 0,
          cycle_milestone_points: s.cycle_milestone_points || 0,
          achievement_points: s.achievement_points || 0,
          bronce_sales: s.bronce_sales || 0,
          plata_sales: s.plata_sales || 0,
          oro_sales: s.oro_sales || 0,
          total_canjes: s.total_canjes || 0,
          total_ahorros: s.total_ahorros || 0,
        }));

        // Sort by total score
        formattedData.sort((a, b) => b.total_score - a.total_score);
        setSellers(formattedData);
      }
    } catch (err) {
      console.error('Error fetching score data:', err);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('es-CR', {
      style: 'currency',
      currency: 'CRC',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount).replace('CRC', '₡');
  };

  const maxScore = useMemo(() => {
    if (sellers.length === 0) return 100;
    return Math.max(...sellers.map(s => s.total_score), 100);
  }, [sellers]);

  const getRankIcon = (index: number) => {
    if (index === 0) return <Trophy className="w-5 h-5 text-yellow-400" />;
    if (index === 1) return <Medal className="w-5 h-5 text-slate-300" />;
    if (index === 2) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="text-white/40 text-sm font-bold">#{index + 1}</span>;
  };

  const getRankGradient = (index: number) => {
    if (index === 0) return 'from-yellow-500/20 to-amber-600/10 border-yellow-500/30';
    if (index === 1) return 'from-slate-400/20 to-slate-500/10 border-slate-400/30';
    if (index === 2) return 'from-amber-700/20 to-orange-800/10 border-amber-600/30';
    return 'from-white/5 to-white/[0.02] border-white/10';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
      </div>
    );
  }

  if (sellers.length === 0) {
    return (
      <div className="text-center py-12">
        <Trophy className="w-12 h-12 text-white/20 mx-auto mb-3" />
        <p className="text-white/60">No hay datos de vendedores disponibles</p>
      </div>
    );
  }

  return (
    <TooltipProvider>
      <div className="space-y-4">
        {/* Header */}
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-gradient-to-br from-amber-500/30 to-orange-500/20 rounded-lg">
            <TrendingUp className="w-5 h-5 text-amber-400" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-white">Resumen de Puntos</h3>
            <p className="text-sm text-white/60">Desglose completo del sistema de puntuación</p>
          </div>
        </div>

        {/* Legend */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-2 p-3 bg-white/5 rounded-xl border border-white/10">
          {SCORE_CATEGORIES.map((cat) => {
            const Icon = cat.icon;
            return (
              <Tooltip key={cat.key}>
                <TooltipTrigger asChild>
                  <div className="flex items-center gap-2 cursor-help">
                    <div className={`p-1.5 rounded ${cat.bgColor}`}>
                      <Icon className={`w-3 h-3 ${cat.color}`} />
                    </div>
                    <span className="text-xs text-white/70">{cat.label}</span>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" className="bg-slate-800 border-white/20 text-white">
                  <p className="text-xs">{cat.description}</p>
                </TooltipContent>
              </Tooltip>
            );
          })}
        </div>

        {/* Sellers List */}
        <div className="space-y-3">
          <AnimatePresence mode="popLayout">
            {sellers.map((seller, index) => {
              const isExpanded = expandedSeller === seller.seller_id;
              const progressPercent = maxScore > 0 ? (seller.total_score / maxScore) * 100 : 0;
              const isCurrentUser = seller.seller_id === user?.id;
              const canExpand = isAdmin || isCurrentUser;

              return (
                <motion.div
                  key={seller.seller_id}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  transition={{ delay: index * 0.05 }}
                  className={`rounded-xl border bg-gradient-to-br ${getRankGradient(index)} overflow-hidden ${
                    isCurrentUser ? 'ring-2 ring-blue-500/50' : ''
                  }`}
                >
                  {/* Main Row */}
                  <button
                    onClick={() => canExpand && setExpandedSeller(isExpanded ? null : seller.seller_id)}
                    className={`w-full p-4 flex items-center gap-3 text-left transition-colors ${
                      canExpand ? 'hover:bg-white/5 cursor-pointer' : 'cursor-default'
                    }`}
                  >
                    {/* Rank */}
                    <div className="flex items-center justify-center w-8 h-8">
                      {getRankIcon(index)}
                    </div>

                    {/* Seller Info */}
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-white truncate">
                          {seller.seller_name}
                        </span>
                        {isCurrentUser && (
                          <span className="px-1.5 py-0.5 text-[10px] bg-blue-500/30 text-blue-300 rounded">
                            Tú
                          </span>
                        )}
                      </div>
                      {seller.ubicacion_tienda && (
                        <div className="flex items-center gap-1 text-xs text-white/50">
                          <MapPin className="w-3 h-3" />
                          {seller.ubicacion_tienda}
                        </div>
                      )}
                    </div>

                    {/* Score Categories Mini - only show for admin or current user */}
                    {(isAdmin || isCurrentUser) && (
                      <div className="hidden md:flex items-center gap-1">
                        {SCORE_CATEGORIES.map((cat) => {
                          const Icon = cat.icon;
                          const points = seller[cat.key as keyof SellerScoreData] as number;
                          if (points === 0) return null;
                          return (
                            <Tooltip key={cat.key}>
                              <TooltipTrigger asChild>
                                <div className={`flex items-center gap-1 px-2 py-1 rounded-lg ${cat.bgColor}`}>
                                  <Icon className={`w-3 h-3 ${cat.color}`} />
                                  <span className={`text-xs font-medium ${cat.color}`}>{points}</span>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top" className="bg-slate-800 border-white/20 text-white">
                                <p className="text-xs">{cat.label}: {points} pts</p>
                              </TooltipContent>
                            </Tooltip>
                          );
                        })}
                      </div>
                    )}

                    {/* Total Score */}
                    <div className="text-right">
                      <div className="text-lg font-bold text-white">{seller.total_score}</div>
                      <div className="text-[10px] text-white/40 uppercase tracking-wider">pts</div>
                    </div>

                    {/* Expand Icon - only show if can expand */}
                    {canExpand && (
                      <div className="text-white/40">
                        {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                      </div>
                    )}
                  </button>

                  {/* Expanded Details */}
                  <AnimatePresence>
                    {isExpanded && (
                      <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.2 }}
                        className="overflow-hidden"
                      >
                        <div className="px-4 pb-4 pt-2 border-t border-white/10 space-y-4">
                          {/* Progress Bar */}
                          <div className="space-y-1">
                            <div className="flex justify-between text-xs">
                              <span className="text-white/50">Progreso vs. líder</span>
                              <span className="text-white/70">{progressPercent.toFixed(0)}%</span>
                            </div>
                            <div className="h-2 bg-white/10 rounded-full overflow-hidden">
                              <motion.div
                                initial={{ width: 0 }}
                                animate={{ width: `${progressPercent}%` }}
                                transition={{ duration: 0.5, delay: 0.2 }}
                                className="h-full bg-gradient-to-r from-blue-500 to-purple-500 rounded-full"
                              />
                            </div>
                          </div>

                          {/* Score Breakdown Grid with Details */}
                          <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                            {/* Membresías */}
                            <div className="p-3 rounded-lg bg-amber-500/20 border border-white/5">
                              <div className="flex items-center gap-2 mb-1">
                                <ShoppingBag className="w-4 h-4 text-amber-400" />
                                <span className="text-xs text-white/60">Membresías</span>
                              </div>
                              <div className="text-xl font-bold text-amber-400">
                                {seller.membership_sale_points}
                                <span className="text-xs font-normal text-white/40 ml-1">pts</span>
                              </div>
                              <div className="mt-2 space-y-0.5 text-[10px] text-white/50">
                                <p>🥉 {seller.bronce_sales} × 10 = {seller.bronce_sales * 10}</p>
                                <p>🥈 {seller.plata_sales} × 20 = {seller.plata_sales * 20}</p>
                                <p>🥇 {seller.oro_sales} × 40 = {seller.oro_sales * 40}</p>
                              </div>
                            </div>

                            {/* Canjes */}
                            <div className="p-3 rounded-lg bg-pink-500/20 border border-white/5">
                              <div className="flex items-center gap-2 mb-1">
                                <Gift className="w-4 h-4 text-pink-400" />
                                <span className="text-xs text-white/60">Canjes</span>
                              </div>
                              <div className="text-xl font-bold text-pink-400">
                                {seller.redemption_points}
                                <span className="text-xs font-normal text-white/40 ml-1">pts</span>
                              </div>
                              <div className="mt-2 text-[10px] text-white/50">
                                <p>{seller.total_canjes} canjes × 8 pts</p>
                              </div>
                            </div>

                            {/* Pasivos */}
                            <div className="p-3 rounded-lg bg-cyan-500/20 border border-white/5">
                              <div className="flex items-center gap-2 mb-1">
                                <Zap className="w-4 h-4 text-cyan-400" />
                                <span className="text-xs text-white/60">Pasivos</span>
                              </div>
                              <div className="text-xl font-bold text-cyan-400">
                                {seller.passive_redemption_points}
                                <span className="text-xs font-normal text-white/40 ml-1">pts</span>
                              </div>
                              <div className="mt-2 text-[10px] text-white/50">
                                <p>Canjes en tus membresías × 2</p>
                              </div>
                            </div>

                            {/* Ahorros - with economic value */}
                            <div className="p-3 rounded-lg bg-green-500/20 border border-white/5">
                              <div className="flex items-center gap-2 mb-1">
                                <DollarSign className="w-4 h-4 text-green-400" />
                                <span className="text-xs text-white/60">Ahorros</span>
                              </div>
                              <div className="text-xl font-bold text-green-400">
                                {seller.savings_points}
                                <span className="text-xs font-normal text-white/40 ml-1">pts</span>
                              </div>
                              <div className="mt-2 text-[10px] text-white/50">
                                <p className="text-green-300/70 font-medium">{formatCurrency(seller.total_ahorros)}</p>
                                <p>÷ 10,000 × 10 pts</p>
                              </div>
                            </div>

                            {/* Hitos del Ciclo */}
                            <div className="p-3 rounded-lg bg-blue-500/20 border border-white/5">
                              <div className="flex items-center gap-2 mb-1">
                                <Target className="w-4 h-4 text-blue-400" />
                                <span className="text-xs text-white/60">Hitos del Ciclo</span>
                              </div>
                              <div className="text-xl font-bold text-blue-400">
                                {seller.cycle_milestone_points}
                                <span className="text-xs font-normal text-white/40 ml-1">pts</span>
                              </div>
                              <div className="mt-2 text-[10px] text-white/50">
                                <p>Bonos mensuales</p>
                                <p className="text-white/30">(Se reinician cada mes)</p>
                              </div>
                            </div>

                            {/* Logros Permanentes */}
                            <div className="p-3 rounded-lg bg-purple-500/20 border border-white/5">
                              <div className="flex items-center gap-2 mb-1">
                                <Award className="w-4 h-4 text-purple-400" />
                                <span className="text-xs text-white/60">Logros Perm.</span>
                              </div>
                              <div className="text-xl font-bold text-purple-400">
                                {seller.achievement_points}
                                <span className="text-xs font-normal text-white/40 ml-1">pts</span>
                              </div>
                              <div className="mt-2 text-[10px] text-white/50">
                                <p>Por trayectoria</p>
                                <p className="text-white/30">(Nunca se pierden)</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </motion.div>
              );
            })}
          </AnimatePresence>
        </div>
      </div>
    </TooltipProvider>
  );
};
